import { getPipelineRunStatusRank } from '../constants/pipelineRunStatus';

// pipelines-list-sort-and-narrow — every ordering decision the Pipelines list makes, as pure
// functions, so the RULES are unit-tested and only their WIRING is left to the grid (AC-1, AC-5,
// AC-7, AC-8).
//
// Nothing here is left to `@mui/x-data-grid`'s default comparator. That is deliberate and is the
// spec's second-named regression risk ("sorting on a lie"): three of these columns are nullable
// in ways a default comparator handles plausibly but wrongly, and a wrong-but-plausible order is
// exactly the kind of bug nobody reports because the page still looks fine.

// Case-insensitive so `alpha` and `Alpha` cluster, which is the whole point of the default order
// (AC-1) — a publisher scanning for their app should not have to know how someone capitalised it.
//
// `localeCompare` with `sensitivity: 'base'` rather than a hand-rolled `toLowerCase()` compare:
// App Instance names are human-entered and may carry accents, and `'é' < 'z'` is only true under
// a locale-aware compare. `undefined` locale = the runtime's, which is the reader's.
const compareTextAscending = (left, right) => String(left).localeCompare(String(right), undefined, { sensitivity: 'base' });

// AC-5 — NULLS LAST ASCENDING, for the App Instance column specifically.
//
// A null AppInstanceName means the owning App Instance is soft-deleted or absent — the
// `(deleted)` rows. They sort LAST ascending because they are orphans: real, reachable, and
// deliberately kept on the page (AC-9 forbids removing them), but not what anyone is scanning
// for. Descending puts them first, which is how you FIND them when that is the job.
//
// ⚠ This is the OPPOSITE of the Latest Run column's null rule (AC-7: never-run sorts FIRST), and
// the difference is intentional, not an inconsistency. The two nulls mean different things: a
// null owner is a data-integrity leftover, while a null run is the most actionable state on a
// worklist. Per-column null semantics is precisely why the spec refused to let the grid decide.
export const compareAppInstanceNameValues = (left, right) => {
	const leftMissing = left == null || left === '';
	const rightMissing = right == null || right === '';
	if (leftMissing && rightMissing) return 0;
	if (leftMissing) return 1;
	if (rightMissing) return -1;
	return compareTextAscending(left, right);
};

// The three plain identity columns (Name, ImageName, ContainerAppName). Empty/absent last, same
// reasoning as above — a blank cell is not a name that sorts before 'a'.
export const comparePipelineTextValues = (left, right) => {
	const leftMissing = left == null || left === '';
	const rightMissing = right == null || right === '';
	if (leftMissing && rightMissing) return 0;
	if (leftMissing) return 1;
	if (rightMissing) return -1;
	return compareTextAscending(left, right);
};

// AC-7/AC-8 — Latest Run orders by the DECLARED rank in constants/pipelineRunStatus.js, never by
// the raw enum value and never alphabetically. Ascending is most-actionable-first, which places
// never-run at the top; the rationale for the whole ordering lives with the constant, so there is
// one place to read it and one place to change it.
export const compareLatestRunStatusValues = (left, right) => getPipelineRunStatusRank(left) - getPipelineRunStatusRank(right);

// AC-1 — THE DEFAULT ORDER: App Instance name ascending, then pipeline name ascending, both
// case-insensitive.
//
// Chosen (spec scope 1) because it delivers the retired accordion's actual benefit — rows
// clustered by App Instance — inside the owner's flat table, and because both keys are STRINGS
// and therefore translatable by the EF SQLite provider as well as SQL Server, so this order
// survives a later server-paging migration unchanged. `CreatedAt` would not: it is a
// DateTimeOffset, the one type that tier cannot translate in ORDER BY (AC-3, spec D5).
//
// STABILITY (AC-1): rows equal on both keys keep their incoming relative order. Array.prototype.
// sort is required to be stable in every engine this app targets (ES2019+), and this comparator
// returns 0 for a genuine tie rather than falling through to some third key — so "stable" here
// means the payload's own order is preserved, which is what makes repeated renders identical.
export const comparePipelineRowsByDefault = (left, right) => {
	const byAppInstance = compareAppInstanceNameValues(left?.AppInstanceName, right?.AppInstanceName);
	if (byAppInstance !== 0) return byAppInstance;
	return comparePipelineTextValues(left?.Name, right?.Name);
};

// AC-2 — the row set as it should FIRST PAINT: never the server's AppInstanceId-GUID order
// (PipelineService.cs:137-149), which is unpredictable to a human because its primary key is a
// GUID.
//
// Returns a NEW array; the input (a redux-derived list) is never sorted in place.
export const sortPipelineRowsByDefault = (rows = []) => [...(rows ?? [])].sort(comparePipelineRowsByDefault);

export default sortPipelineRowsByDefault;
